/**
 * The public projection (design doc sections 22 to 27): what status.hipnum.com
 * is allowed to say. Customers care whether Live Bidding works; they do not
 * need `auction-ring-runtime-consumer-2`. So the public vocabulary is a
 * separate, smaller one, an internal component maps onto it, and the public
 * text starts as a deterministic template. AI may draft later; a person or a
 * policy publishes.
 *
 * Nothing in this file names a tenant, a host, a SHA or a number from a
 * diagnostic. That is the point of it being a separate file.
 */

export const PUBLIC_COMPONENTS = [
  { id: 'AUCTION_PLATFORM', title: 'Auction Platform', description: 'Signing in, catalogs and registration.' },
  { id: 'LIVE_BIDDING', title: 'Live Bidding', description: 'Placing and seeing bids during a live sale.' },
  { id: 'AUCTION_WEBSITES', title: 'Auction Websites', description: 'Sale company websites and catalogs on hipnum.com.' },
  { id: 'AUCTION_ADMINISTRATION', title: 'Auction Administration', description: 'The office and clerk tools.' },
  { id: 'PAYMENTS', title: 'Payments & Invoicing', description: 'Invoices, checkout and settlement.' },
  { id: 'SMS', title: 'SMS & Notifications', description: 'Text messages and alerts.' },
  { id: 'MEDIA', title: 'Media & Broadcast', description: 'Photos, video and broadcast graphics.' },
] as const;
export type PublicComponentId = (typeof PUBLIC_COMPONENTS)[number]['id'];
export const PUBLIC_COMPONENT_IDS = PUBLIC_COMPONENTS.map((c) => c.id) as [PublicComponentId, ...PublicComponentId[]];

/** Public component status (section 24). Overall is the worst of these. */
export const PUBLIC_STATUSES = ['OPERATIONAL', 'DEGRADED', 'PARTIAL_OUTAGE', 'MAJOR_OUTAGE', 'MAINTENANCE'] as const;
export type PublicStatus = (typeof PUBLIC_STATUSES)[number];

/** What an incident does to a component while it is open (the status it projects). */
export const PUBLIC_IMPACTS = ['DEGRADED', 'PARTIAL_OUTAGE', 'MAJOR_OUTAGE'] as const;
export type PublicImpact = (typeof PUBLIC_IMPACTS)[number];

/** Public incident states (section 26). */
export const PUBLIC_STATES = ['INVESTIGATING', 'IDENTIFIED', 'MONITORING', 'RESOLVED'] as const;
export type PublicState = (typeof PUBLIC_STATES)[number];

/** Internal component (impact.ts SERVICE_COMPONENTS) -> the public components it is part of (section 66). */
export const PUBLIC_COMPONENTS_FOR_SERVICE: Readonly<Record<string, readonly PublicComponentId[]>> = {
  AUCTION_CORE: ['LIVE_BIDDING'],
  BID_ENGINE: ['LIVE_BIDDING'],
  EVENT_STORE: ['LIVE_BIDDING'],
  DATABASE: ['AUCTION_PLATFORM', 'LIVE_BIDDING', 'AUCTION_ADMINISTRATION', 'PAYMENTS'],
  CONTROL_API: ['AUCTION_PLATFORM', 'AUCTION_ADMINISTRATION'],
  AUTH: ['AUCTION_PLATFORM'],
  PAYMENTS: ['PAYMENTS'],
  STOREFRONT: ['AUCTION_WEBSITES'],
  SETTLEMENT: ['PAYMENTS'],
  ADMIN: ['AUCTION_ADMINISTRATION'],
  SMS: ['SMS'],
  ANNIE: ['SMS'],
  MEDIA: ['MEDIA'],
  GFX: ['MEDIA'],
  MARKETING: [],
  DISPLAY: ['MEDIA'],
};

export function publicComponentsFor(serviceIds: readonly string[]): PublicComponentId[] {
  const out = new Set<PublicComponentId>();
  for (const s of serviceIds) for (const c of PUBLIC_COMPONENTS_FOR_SERVICE[s] ?? []) out.add(c);
  return PUBLIC_COMPONENT_IDS.filter((c) => out.has(c));
}

const titleOf = (id: PublicComponentId): string => PUBLIC_COMPONENTS.find((c) => c.id === id)?.title ?? id;

/** Deterministic public wording (section 27). No root cause, no numbers, no names. */
export function publicTitleFor(impact: PublicImpact, components: readonly PublicComponentId[]): string {
  const what = components.length === 0 ? 'the platform' : components.map(titleOf).join(', ');
  switch (impact) {
    case 'DEGRADED': return `Degraded performance affecting ${what}`;
    case 'PARTIAL_OUTAGE': return `Partial outage affecting ${what}`;
    case 'MAJOR_OUTAGE': return `Outage affecting ${what}`;
  }
}

export function publicMessageFor(state: PublicState, impact: PublicImpact, components: readonly PublicComponentId[]): string {
  const what = components.length === 0 ? 'the platform' : components.map(titleOf).join(', ');
  const noun = impact === 'DEGRADED' ? 'degraded performance' : impact === 'PARTIAL_OUTAGE' ? 'a partial outage' : 'an outage';
  switch (state) {
    case 'INVESTIGATING': return `We are investigating ${noun} affecting ${what}.`;
    case 'IDENTIFIED': return `We have identified the cause of ${noun} affecting ${what} and are working on a fix.`;
    case 'MONITORING': return `A fix has been applied for ${what}. We are monitoring the results.`;
    case 'RESOLVED': return `This incident has been resolved. ${what} is operating normally.`;
  }
}

/** The status a component shows while an incident with this impact is open on it. */
export const statusForImpact = (impact: PublicImpact): PublicStatus => impact;

const STATUS_RANK: Record<PublicStatus, number> = { OPERATIONAL: 0, MAINTENANCE: 1, DEGRADED: 2, PARTIAL_OUTAGE: 3, MAJOR_OUTAGE: 4 };

/** Overall status is the worst component status; nothing at all is OPERATIONAL only when there are components to be operational. */
export function overallStatus(statuses: readonly PublicStatus[]): PublicStatus {
  let worst: PublicStatus = 'OPERATIONAL';
  for (const s of statuses) if (STATUS_RANK[s] > STATUS_RANK[worst]) worst = s;
  return worst;
}
